import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { hasAtLeast, isGlobalAdmin } from "../auth/roles";
import { useAuth } from "../auth/AuthContext";
import { PortalLayout } from "../components/Portal";
import { Btn, Spinner, StatusBadge, ProgressBar, Th, ConfirmDialog, cellClip, formatWhen, useSortableColumns, useColumnWidths } from "../components/Shared";
import { useDisplayTimezone } from "../portalConfig";
import { fmtBytes } from "../wizard/SubmissionWizard";
import { serverNow } from "../serverClock";

const MONO = "'JetBrains Mono', monospace";

// A failure message says what happened, how far the transfer got and why — too
// much for the Status column, so it is cut here and carried in full by `title`.
const ERROR_PREVIEW_CHARS = 60;

const KIND_LABEL = {
  submission: "File upload",
  results:    "Results packaging",
};

// How often a running job's progress is re-read. Matches the cadence the
// Results tab already polls at, so the two views don't tick against each other.
const POLL_MS = 5000;

// The clock lives in serverClock.js and is fed by api.js from the HTTP Date
// header. It used to be learned here from a running job's `updated_at`, which
// is a live server reading only for the streaming upload loop — a results
// packaging row never moved, and because the offset is module state one such
// row made every job on this page read "0s".
//
// Re-exported because jobDuration.test.jsx and other callers import them from
// here alongside `duration`.
export { noteServerTime, serverNow, __setServerOffset } from "../serverClock";

/**
 * Elapsed time of a job.
 *
 * **Never subtract a server timestamp from the browser's clock.** That is what
 * produced "0s" on a running upload: the two clocks were ~24 s apart, the
 * difference went negative, and Math.max(0, …) pinned it to zero until real
 * time caught up. It also over-reports in the other direction — CLAUDE.md
 * records a 15-minute upload once shown as 2h 15m.
 *
 * A finished job compares two server timestamps and is already immune; a
 * running one now measures against `serverNow()` so that every viewer, whatever
 * their clock, sees the same number.
 */
export function duration(from, to) {
  if (!from) return "—";
  const end = to ? new Date(to).getTime() : serverNow();
  const seconds = Math.max(0, Math.round((end - new Date(from).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

const JOB_ACCESSORS = {
  created: j => new Date(j.created_at).getTime(),
  kind:    j => KIND_LABEL[j.kind] || j.kind,
  project: j => j.project_name || "",
  who:     j => j.created_by_email || "",
  status:  j => j.status,
  // serverNow(), not new Date(): sorting by duration mixed the two clocks
  // the same way the column did, so a running job sorted by the viewer's skew.
  took:    j => (j.finished_at ? new Date(j.finished_at).getTime() : serverNow()) - new Date(j.created_at).getTime(),
};

// How long a running job may be silent before the page says so. Two minutes,
// because every running job writes progress every few seconds — an upload
// re-stamps on each accepted part and packaging on each file — so two minutes of
// nothing already means something, while being far short of the 30 minutes the
// server waits before it will hand the work to another pod.
const STALLED_AFTER_MS = 2 * 60 * 1000;

function stalledFor(job) {
  if (job.status !== "running" || !job.updated_at) return 0;
  // serverNow(), not new Date(): the duration column learned this the hard way,
  // and a viewer whose clock is two minutes fast would otherwise see every
  // healthy job reported as stalled.
  const silent = serverNow() - new Date(job.updated_at).getTime();
  return silent > STALLED_AFTER_MS ? silent : 0;
}

function StalledNote({ job, zone }) {
  const silent = stalledFor(job);
  if (!silent) return null;
  const mins = Math.floor(silent / 60000);
  // **Why this exists.** A packaging run whose pod dies keeps its row at
  // `running`, with the progress bar frozen and `claimed_by` naming a pod that
  // no longer exists — and the server will not hand it to another pod until it
  // has been silent for 30 minutes, on a sweep that runs every 15. Measured on
  // the cluster on 2026-09-07: 45 minutes from the pod dying to the work
  // restarting, during which the screen showed nothing to distinguish it from a
  // slow job.
  //
  // The operator's instinct in that gap is to package the folder again, and that
  // now writes a SECOND archive — `archive_key` carries the Result's id — which
  // is the duplicate this portal refuses at the button precisely so nobody ends
  // up with two. Saying "it will be picked up automatically" is the whole fix.
  return (
    <div style={{ fontSize: 11, color: "#b45309", marginTop: 4 }}>
      ⚠ No progress for {mins} minute{mins === 1 ? "" : "s"}.
      {stalledAdvice(job, zone)}
    </div>
  );
}

// What a silent job's reader should do next, by kind.
//
// A browser upload (`submission`) stops because the browser stopped sending —
// a reloaded or closed tab, a sleeping laptop — and the server knows when
// (`updated_at`: no slice since) and how long it keeps the parts
// (`resume_expires_at`). A 77 GB upload on dev stopped at 26 % on 2026-10-06
// and the page said only "No progress for 11 minutes", read as "the server is
// broken". Resuming needs the same file chosen again, because a browser cannot
// re-read a File after a reload; so the sentence names it.
//
// `results_upload` gets its own sentence: the backend stamps resume_expires_at
// on it too (the data path is shared), but /submit/resumable lists only
// `submission`, so pointing an operator at the wizard sent them nowhere.
function stalledAdvice(job, zone) {
  if (job.kind === "results") {
    return " If the run stopped, it is picked up again automatically — do not start it a second time, that would build a second archive.";
  }
  if (job.kind === "results_upload") {
    return " If the transfer stopped, it cannot be resumed — cancel it and upload the file again from the project's Results tab.";
  }
  if (!job.resume_expires_at) {
    return " If the transfer stopped, it can be resumed from the wizard.";
  }
  const stoppedAt = formatWhen(job.updated_at, { zone });
  const deadline = formatWhen(job.resume_expires_at, { zone });
  if (new Date(job.resume_expires_at).getTime() <= serverNow()) {
    return ` The browser stopped sending at ${stoppedAt}, and the parts were kept only until ${deadline}: this upload can no longer be resumed. Start a new submission.`;
  }
  const file = job.current_file || job.label;
  // The file in flight is not yet done, so it is one of the remaining ones.
  const after = Math.max(0, (job.total_files || 0) - (job.done_files || 0) - 1);
  const rest = after ? ` (and the ${after} more file${after === 1 ? "" : "s"} not yet sent)` : "";
  return ` The browser stopped sending at ${stoppedAt}. To resume, open the submission wizard on this project and choose ${file ? `“${file}”` : "the same file"}${rest} again before ${deadline}.`;
}

function ProgressCell({ job, zone }) {
  if (job.status !== "running") {
    // Not gated on kind: a finished packaging run knows its file count too,
    // and used to render "—" despite having just archived 144 files.
    if (job.total_files) {
      return <span style={{ fontSize: 12, color: "#6b7280" }}>{job.done_files || 0} / {job.total_files} file(s)</span>;
    }
    return <span style={{ color: "#6b7280", fontSize: 12 }}>—</span>;
  }
  // Without a total there is nothing honest to draw a bar against — show the
  // bytes moved so far instead of a bar that would imply a known endpoint.
  if (!job.total_bytes) {
    return (
      <div>
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {job.current_file ? `${job.current_file} · ` : ""}{fmtBytes(job.done_bytes || 0)}
        </span>
        <StalledNote job={job} zone={zone} />
      </div>
    );
  }
  const pct = Math.min(100, ((job.done_bytes || 0) / job.total_bytes) * 100);
  return (
    <div>
      <ProgressBar value={pct} label={`${KIND_LABEL[job.kind] || job.kind}, ${Math.round(pct)}%`} />
      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
        {fmtBytes(job.done_bytes || 0)} / {fmtBytes(job.total_bytes)} ({Math.round(pct)}%)
      </div>
      <StalledNote job={job} zone={zone} />
    </div>
  );
}

export default function JobsPage() {
  const zone = useDisplayTimezone();
  const { user } = useAuth();
  const [jobs, setJobs]       = useState(null);
  const [error, setError]     = useState(null);
  const [cancelling, setCancelling] = useState({});
  const [clearInfo, setClearInfo]   = useState(null);   // the confirm dialog, when open
  const [clearing, setClearing]     = useState(false);
  const [notice, setNotice]         = useState(null);
  const [recordInfo, setRecordInfo] = useState(null);   // the job awaiting confirmation
  const [recording, setRecording]   = useState({});
  const { sorted, sortKey, sortDir, toggleSort } = useSortableColumns(jobs || [], JOB_ACCESSORS);
  const { widths, setWidth, totalWidth } = useColumnWidths({
    created: 170, kind: 150, project: 180, who: 130, status: 150, progress: 230, took: 90, actions: 110,
  });

  const load = useCallback(() => {
    api.get("/api/jobs")
      .then(data => {
        // No clock handling here: api.js has already learned the server's time
        // from this very response's Date header.
        setJobs(data); setError(null);
      })
      .catch(err => setError(err.message));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Admin-only, and finished jobs only — the server refuses to touch a running
  // one, because deleting its row would leave the upload streaming with no
  // Cancel button and an unreadable cancel flag.
  // Counts come from the server, not from the page: the table shows what this
  // user can see, while the clear acts on every job there is.
  const openClear = async older => {
    try {
      const c = await api.get("/api/admin/jobs/clearable");
      setClearInfo({ ...c, older_count: c.older, older });
    } catch (err) { setError(err.message); }
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      const q = clearInfo?.older ? `?older_than_days=${clearInfo.keep_days}` : "";
      const r = await api.delete(`/api/admin/jobs${q}`);
      setClearInfo(null);
      // Say what actually happened rather than assuming: if an upload was
      // running it is still there, and the table will still show it.
      setNotice(r?.kept_running
        ? `Cleared ${r.deleted} finished job(s). ${r.kept_running} still running and kept.`
        : `Cleared ${r?.deleted ?? 0} finished job(s).`);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setClearing(false);
    }
  };

  const handleCancel = async job => {
    // Worded by kind, because the two cancels do opposite things. An upload's
    // cleanup removes what it wrote; a packaging run wrote nothing but a
    // partial archive nobody can see, and its s3_prefix is the facility's own
    // source data — promising to remove it would be both wrong and alarming.
    const prompt = job.kind === "results"
      ? `Stop packaging this delivery?\n\nNothing is deleted from storage: the partial archive ` +
        `is discarded and the source files are left exactly as they are. You can package it again.`
      : `Cancel this upload?\n\nEverything already uploaded under ${job.job_id} will be removed ` +
        `from storage. Other submissions are not affected.`;
    if (!window.confirm(prompt)) return;
    setCancelling(c => ({ ...c, [job.id]: true }));
    try {
      await api.post(`/api/jobs/${job.id}/cancel`);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(c => ({ ...c, [job.id]: false }));
    }
  };

  // Writes the Submission row for an upload whose files reached storage but
  // whose final call never did. The server re-checks storage before recording
  // anything and refuses if it does not find the files — this button cannot
  // invent a submission, and it deletes and moves nothing.
  const handleRecord = async () => {
    const job = recordInfo;
    if (!job) return;
    setRecording(r => ({ ...r, [job.id]: true }));
    try {
      const r = await api.post(`/api/jobs/${job.id}/finish`);
      setRecordInfo(null);
      setNotice(r?.already_recorded
        ? `${job.job_id} was already recorded.`
        : `${job.job_id} recorded as a submission. Nothing was uploaded again.`);
      load();
    } catch (err) {
      setError(err.message);
      setRecordInfo(null);
    } finally {
      setRecording(r => ({ ...r, [job.id]: false }));
    }
  };

  const scope = isGlobalAdmin(user) ? "Everything running on this portal"
    : hasAtLeast(user, "operator") ? "Work in your group"
    : "Your uploads and deliveries";
  const running = (jobs || []).filter(j => j.status === "running");

  return (
    <PortalLayout>
      <div data-page-header style={{ display: "flex", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Jobs</h1>
          <p style={{ margin: "4px 0 0", fontSize: 14, color: "#6b7280", maxWidth: 720 }}>
            {scope}. Uploads and results packaging run on the server, so they keep going after you
            close the tab — this is where you pick them back up.
            {running.length > 0 && (
              <> <strong>{running.length} {running.length === 1 ? "job is" : "jobs are"} running right now.</strong></>
            )}
          </p>
        </div>
        {/* Admin only, mirroring the Log page. The age one comes first because
            it is the one that is almost always right; both go through the same
            dialog, which states the count the server reports for that choice. */}
        {user?.is_admin && (
          <div data-page-actions style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <Btn variant="secondary" onClick={() => openClear(true)}>
              Clear over 90 days
            </Btn>
            <Btn variant="secondary" onClick={() => openClear(false)}
              style={{ color: "#dc2626", borderColor: "#fca5a5" }}>
              Clear all finished
            </Btn>
          </div>
        )}
      </div>

      {clearInfo && (
        <ConfirmDialog
          title="Clear job history?"
          confirmWord="clear"
          confirmLabel="Clear history"
          busy={clearing}
          onConfirm={handleClear}
          onClose={() => setClearInfo(null)}
        >
          <p style={{ margin: "0 0 10px" }}>
            This removes <strong>{clearInfo.older ? clearInfo.older_count : clearInfo.all_finished}</strong>{" "}
            finished job{(clearInfo.older ? clearInfo.older_count : clearInfo.all_finished) === 1 ? "" : "s"}
            {clearInfo.older ? ` finished more than ${clearInfo.keep_days} days ago` : ""}. It deletes
            no data — uploads and deliveries are unaffected, and the audit log keeps a record that the
            history was cleared, including why any upload failed.
          </p>
          {clearInfo.running > 0 && (
            <p style={{ margin: "0 0 10px", color: "#0b6b33" }}>
              <strong>{clearInfo.running} running job{clearInfo.running === 1 ? " is" : "s are"} kept.</strong>{" "}
              A running job cannot be cleared — removing its row would leave the transfer going with
              no way to cancel it.
            </p>
          )}
          {clearInfo.unrecorded > 0 && (
            <p style={{ margin: "0 0 10px", color: "#0b6b33" }}>
              <strong>{clearInfo.unrecorded} upload{clearInfo.unrecorded === 1 ? "" : "s"} whose data is in
              storage but not yet recorded {clearInfo.unrecorded === 1 ? "is" : "are"} kept.</strong>{" "}
              Clearing those would delete the only record of where that data is.
            </p>
          )}
          <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>
            Type <strong>clear</strong> to confirm.
          </p>
        </ConfirmDialog>
      )}

      {recordInfo && (
        <ConfirmDialog
          title="Record this upload as a submission?"
          confirmLabel="Record upload"
          busyLabel="Recording…"
          // Nothing is deleted, moved or re-uploaded here — this only writes the
          // record for files already in storage — so it asks for a click rather
          // than a typed word, and does not dress itself up as a destruction.
          tone="safe"
          busy={!!recording[recordInfo.id]}
          onConfirm={handleRecord}
          onClose={() => setRecordInfo(null)}
        >
          <p style={{ margin: "0 0 10px" }}>
            <strong>{recordInfo.streamed_file_count}</strong>{" "}
            file{recordInfo.streamed_file_count === 1 ? "" : "s"} from{" "}
            <strong>{recordInfo.job_id}</strong> reached storage but were never recorded, so they do
            not appear under the project. This writes that record.
          </p>
          <p style={{ margin: "0 0 10px", color: "#0b6b33" }}>
            Nothing is uploaded again, and <strong>nothing is moved or deleted</strong>. The portal
            checks the files are still in storage first and refuses if they are not.
          </p>
          {recordInfo.total_files > recordInfo.streamed_file_count && (
            <p style={{ margin: 0, color: "#b45309", fontSize: 13 }}>
              This upload declared {recordInfo.total_files} file
              {recordInfo.total_files === 1 ? "" : "s"}; only {recordInfo.streamed_file_count}{" "}
              reached storage. The rest never arrived and will have to be uploaded again.
            </p>
          )}
        </ConfirmDialog>
      )}

      {notice && (
        <div role="status" style={{ padding: "10px 14px", borderRadius: 8, background: "#f3f4f6", border: "1px solid #d1d5db", color: "#374151", fontSize: 13, marginBottom: 20 }}>
          {notice}
        </div>
      )}

      {error && (
        <div role="alert" style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13, marginBottom: 20 }}>
          {error}
        </div>
      )}

      {jobs === null ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#6b7280" }}><Spinner /> Loading…</div>
      ) : jobs.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 0", color: "#6b7280" }}>
          Nothing has been submitted yet.
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table className="responsive-table" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: totalWidth }}>
              <caption style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>
                Uploads and results packaging, most recent first
              </caption>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                  <Th label="Started"  sortKey="created" activeKey={sortKey} dir={sortDir} onSort={toggleSort} width={widths.created} onWidth={w => setWidth("created", w)} />
                  <Th label="Job"      sortKey="kind"    activeKey={sortKey} dir={sortDir} onSort={toggleSort} width={widths.kind}    onWidth={w => setWidth("kind", w)} />
                  <Th label="Project"  sortKey="project" activeKey={sortKey} dir={sortDir} onSort={toggleSort} width={widths.project} onWidth={w => setWidth("project", w)} />
                  <Th label="Started by" sortKey="who"   activeKey={sortKey} dir={sortDir} onSort={toggleSort} width={widths.who}     onWidth={w => setWidth("who", w)} />
                  <Th label="Status"   sortKey="status"  activeKey={sortKey} dir={sortDir} onSort={toggleSort} width={widths.status}  onWidth={w => setWidth("status", w)} />
                  <Th label="Progress" width={widths.progress} onWidth={w => setWidth("progress", w)} />
                  <Th label="Duration" sortKey="took"    activeKey={sortKey} dir={sortDir} onSort={toggleSort} width={widths.took}    onWidth={w => setWidth("took", w)} />
                  <Th label=""         width={widths.actions} onWidth={w => setWidth("actions", w)} />
                </tr>
              </thead>
              <tbody>
                {sorted.map(job => (
                  <tr key={job.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td data-label="Started" style={{ padding: "9px 14px", fontSize: 12, color: "#6b7280", whiteSpace: "nowrap", fontFamily: MONO }}>
                      {formatWhen(job.created_at, { zone })}
                    </td>
                    <td data-label="Job" style={{ padding: "9px 14px", fontSize: 13, ...cellClip }}>
                      {KIND_LABEL[job.kind] || job.kind}
                      {job.label && (
                        // whiteSpace:nowrap via cellClip — without it the
                        // textOverflow here had nothing to truncate and the
                        // ellipsis never appeared.
                        <div style={{ color: "#6b7280", fontSize: 11, fontFamily: MONO, ...cellClip }}
                             title={job.label}>
                          {job.label}
                        </div>
                      )}
                    </td>
                    <td data-label="Project" style={{ padding: "9px 14px", fontSize: 13, ...cellClip }}>
                      <Link to={`/projects/${job.project_id}`} style={{ color: "#1d4ed8" }}
                            title={job.project_name || undefined}>
                        {job.project_name || `#${job.project_id}`}
                      </Link>
                      {job.job_id && (
                        <div style={{ color: "#6b7280", fontSize: 11, fontFamily: MONO }}>{job.job_id}</div>
                      )}
                    </td>
                    <td data-label="Started by" style={{ padding: "9px 14px", fontSize: 12, color: "#6b7280", fontFamily: MONO, ...cellClip }}
                        title={job.created_by_email || undefined}>
                      {job.created_by_email || "—"}
                    </td>
                    <td data-label="Status" style={{ padding: "9px 14px" }}>
                      <StatusBadge status={job.status} />
                      {job.error && (
                        // Truncated with the full text on hover, the same way
                        // the Results table shows a packaging error. These
                        // messages are deliberately long — they name the file,
                        // how far it got and why — and printing one in full
                        // inside a 150px column pushed the row apart.
                        <div style={{ color: "#b91c1c", fontSize: 11, marginTop: 4 }} title={job.error}>
                          {job.error.length > ERROR_PREVIEW_CHARS
                            ? `${job.error.slice(0, ERROR_PREVIEW_CHARS)}…`
                            : job.error}
                        </div>
                      )}
                    </td>
                    <td data-label="Progress" style={{ padding: "9px 14px" }}>
                      <ProgressCell job={job} zone={zone} />
                    </td>
                    <td data-label="Duration" style={{ padding: "9px 14px", fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>
                      {duration(job.created_at, job.finished_at)}
                      {/* Says the number is still climbing — "Took" read as a
                          final figure on a job that hadn't finished. */}
                      {job.status === "running" && (
                        <div style={{ fontSize: 11, color: "#6b7280" }}>so far</div>
                      )}
                    </td>
                    <td data-label="" style={{ padding: "9px 14px" }}>
                      {/* Both kinds are cancellable now: the packaging loop
                          polls cancel_requested between parts, the same way the
                          streaming upload does. */}
                      {job.status === "running" && (
                        <Btn variant="secondary" onClick={() => handleCancel(job)} disabled={!!cancelling[job.id]}
                          style={{ padding: "5px 12px", fontSize: 12 }}>
                          {cancelling[job.id] ? "Cancelling…" : "Cancel"}
                        </Btn>
                      )}
                      {/* The files are already in storage; this writes the
                          record that never got written. `recoverable` is the
                          server's judgement, not the page's — it knows whether
                          a submission already exists for the job. */}
                      {job.recoverable && (
                        <Btn variant="secondary" onClick={() => setRecordInfo(job)}
                          disabled={!!recording[job.id]}
                          style={{ padding: "5px 12px", fontSize: 12 }}>
                          {recording[job.id] ? "Recording…" : "Record upload"}
                        </Btn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: "10px 16px", fontSize: 12, color: "#6b7280", borderTop: "1px solid #f3f4f6" }}>
            {jobs.length} job{jobs.length !== 1 ? "s" : ""}
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
